import { useCallback, useEffect } from 'react';
import useGameStore from '@store/useGameStore';
import useCinematicStore from '@store/useCinematicStore';
import useMapStore from '@store/useMapStore';
import { EMPTY_ARRAY, getTileSize } from '@utils/constants';
import HorizontalSlider from '@components/ui/HorizontalSlider';
import EventCard from './EventCard';
import './CinematicPanel.css';

// Helper para calcular el centro de un evento (promedio si hay múltiples posiciones)
const getEventCenterPosition = (event) => {
  const positions = [];

  // 1. target_data tiene coordenadas directas (para acciones con target)
  if (event.target_data?.x !== undefined) {
    positions.push({ x: event.target_data.x, y: event.target_data.y });
  }

  // 2. state_delta.units tiene coordenadas de unidades afectadas
  if (event.state_delta?.units) {
    Object.values(event.state_delta.units).forEach(unit => {
      if (unit.x !== undefined && unit.y !== undefined) {
        positions.push({ x: unit.x, y: unit.y });
      }
    });
  }

  // 3. Fallback: buscar unidades de card_entities en gameStore
  if (positions.length === 0 && event.card_entities?.length > 0) {
    const gameStore = useGameStore.getState();
    event.card_entities.forEach(entity => {
      if (entity.type === 'unit') {
        const unit = gameStore.getUnitById(entity.id);
        if (unit?.x !== undefined) {
          positions.push({ x: unit.x, y: unit.y });
        }
      }
    });
  }

  if (positions.length === 0) return null;

  const avgX = positions.reduce((sum, p) => sum + p.x, 0) / positions.length;
  const avgY = positions.reduce((sum, p) => sum + p.y, 0) / positions.length;
  return { x: Math.round(avgX), y: Math.round(avgY) };
};

const CinematicPanel = () => {
  // --- Game state check ---
  const isResolutionPhase = useGameStore((state) => state.isResolutionPhase);

  // --- Subscribe to cinematic store ---
  const currentActionCardIndex = useCinematicStore((state) => state.currentActionCardIndex);
  const executedUpToIndex = useCinematicStore((state) => state.executedResolutionQueueIndex);
  const isAnimating = useCinematicStore((state) => state.isAnimating);
  const isAutoPlay = useCinematicStore((state) => state.isAutoPlay) || false;

  // Store actions
  const selectCard = useCinematicStore((state) => state.selectCard);
  const executeCard = useCinematicStore((state) => state.executeCard);
  const setAutoPlay = useCinematicStore((state) => state.setAutoPlay);
  const nextCard = useCinematicStore((state) => state.nextCard);
  const previousCard = useCinematicStore((state) => state.previousCard);

  // --- Map store for centering ---
  const centerOnTile = useMapStore((state) => state.centerOnTile);

  // --- Auto-center map when currentActionCardIndex changes ---
  useEffect(() => {
    const rq = useGameStore.getState().getResolutionQueue() || [];
    const event = rq[currentActionCardIndex];
    if (!event) return;

    const center = getEventCenterPosition(event);
    if (center) {
      centerOnTile(center.x, center.y, getTileSize());
    }
  }, [currentActionCardIndex, centerOnTile]);

  // --- Get resolution queue ---
  const resolutionQueue = useGameStore.getState().getResolutionQueue() || EMPTY_ARRAY;

  // --- Click handler for action cards ---
  const handleActionCardClick = useCallback((clickedIndex) => {
    console.log('[CinematicPanel] handleActionCardClick called', { clickedIndex });
    const state = useCinematicStore.getState();
    if (clickedIndex === state.currentActionCardIndex && clickedIndex > state.executedResolutionQueueIndex) {
      console.log('[CinematicPanel] Executing card');
      executeCard();
    } else {
      console.log('[CinematicPanel] Selecting card');
      selectCard(clickedIndex);
    }
  }, [selectCard, executeCard]);

  // --- Click handler for final "current" card ---
  const handleFinalCardClick = useCallback(() => {
    const endCardIndex = resolutionQueue.length;
    selectCard(endCardIndex);
  }, [resolutionQueue.length, selectCard]);

  // --- Play/Pause handler (for keyboard shortcuts) ---
  const handlePlayPause = useCallback(() => {
    if (isAutoPlay) {
      setAutoPlay(false);
    } else {
      setAutoPlay(true);
      if (!isAnimating) {
        executeCard();
      }
    }
  }, [isAutoPlay, isAnimating, setAutoPlay, executeCard]);

  // --- Keyboard shortcuts ---
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }

      switch (e.key) {
        case ' ':
          e.preventDefault();
          handlePlayPause();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          previousCard();
          break;
        case 'ArrowRight':
          e.preventDefault();
          nextCard();
          break;
        case 'Enter':
          e.preventDefault();
          if (!isAnimating) {
            executeCard();
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handlePlayPause, previousCard, nextCard, executeCard, isAnimating]);

  // --- Early exit if not in resolution phase ---
  if (!isResolutionPhase) {
    return null;
  }

  // --- Render ---
  return (
    <div className="footer-panel-container footer-panel-content">
      <HorizontalSlider className="card-track" scrollToIndex={currentActionCardIndex}>
        {/* Resolution Queue Events */}
        {resolutionQueue.map((event, index) => {
          const isSelected = index === currentActionCardIndex;
          const isExecuted = index <= executedUpToIndex;
          const isCurrentlyAnimating = index === currentActionCardIndex && isAnimating;

          return (
            <EventCard
              key={event.id || `event-${index}`}
              event={event}
              index={index}
              isSelected={isSelected}
              isExecuted={isExecuted}
              isAnimating={isCurrentlyAnimating}
              showPlayButton={isSelected && !isAnimating && index > executedUpToIndex}
              onClick={() => handleActionCardClick(index)}
            />
          );
        })}

        {/* End State Card */}
        <EventCard
          key="end-state"
          event={{
            id: 'artificial-end',
            isArtificial: true,
            type: 'current_state',
            action: { name: 'Final State' }
          }}
          index="current"
          isSelected={currentActionCardIndex === resolutionQueue.length}
          isExecuted={executedUpToIndex === resolutionQueue.length - 1}
          isAnimating={false}
          showPlayButton={false}
          onClick={handleFinalCardClick}
        />
      </HorizontalSlider>
    </div>
  );
};

export default CinematicPanel;
